import { assertUnreachable } from "@/utils";
import {
  ActionInternal,
  KeyboardConfigInternal,
  isMultipleActionsInternal,
  isHoldTapInternal,
} from "@/keyboard/internal_config";
import { isMultipleKeyCodes } from "@/keyboard/action_utils";
import { Condition, Keys, LedRule } from "@/keyboard/keyboard_config";
import {
  isCondAnd,
  isCondLogic,
  isCondNot,
  isCondOr,
  isKeysAll,
  isKeysCols,
  isKeysKeys,
  isKeysRows,
} from "@/keyboard/leds";
import { MouseConfig } from ".";

const SIZES = {
  // KeyboardConfig
  config: 24,
  // LedRule
  led_rule: 36,
  keys: 12,
  condition: 12,
  transition: 6,
  // MouseConfig
  mouse_config: 44,
  speed_profile: 10, // multiple pointed at in mouse_config
  // Layers
  action: 8,
  key_code: 1,
  hold_tap_action: 28,
} as const;

const sum = (arr: number[]) => arr.reduce((acc, x) => acc + x);

const keysAdditionalSize = (keys?: Keys | null): number =>
  keys == undefined || isKeysAll(keys)
    ? 0
    : SIZES.keys +
      (isKeysRows(keys)
        ? keys.Rows.length
        : isKeysCols(keys)
        ? keys.Cols.length
        : isKeysKeys(keys)
        ? keys.Keys.length * 2
        : assertUnreachable(keys));

// Recursive to get any additional Condition due to Not/And/Or
const conditionSize = (c: Condition): number =>
  SIZES.condition +
  (isCondLogic(c)
    ? isCondAnd(c)
      ? sum(c.And.map(conditionSize))
      : isCondOr(c)
      ? sum(c.Or.map(conditionSize))
      : isCondNot(c)
      ? conditionSize(c.Not)
      : assertUnreachable(c)
    : 0);

const ledRuleSize = (rule: LedRule): number => {
  const transitions_size = rule.pattern.transitions.length * SIZES.transition;
  // Subtract size of the Condition already included in LedRule
  const condition_size = conditionSize(rule.condition) - SIZES.condition;
  return (
    SIZES.led_rule +
    keysAdditionalSize(rule.keys) +
    transitions_size +
    condition_size
  );
};

// Size of each LedRule + size of each config &[LedRule] slice
const ledsConfigSize = (leds: LedRule[][]): number =>
  8 * leds.length + sum(leds.flat(2).map(ledRuleSize));

const mouseConfigSize = (_mouse: MouseConfig): number =>
  SIZES.mouse_config + 4 * SIZES.speed_profile;

const actionSize = (a: ActionInternal): number =>
  SIZES.action +
  (isMultipleKeyCodes(a)
    ? 8 + SIZES.key_code * a.MultipleKeyCodes.length
    : isMultipleActionsInternal(a)
    ? 8 + sum(a.MultipleActions.map(actionSize))
    : isHoldTapInternal(a)
    ? SIZES.hold_tap_action
    : 0);

// A const array so no additional pointer cost, just sum of actions
const layersSize = (layers: ActionInternal[][][]): number =>
  sum(layers.flat(3).map(actionSize));

// This should be the upper estimate, because some things behind references
// may be optimized to point to the same object.
// TODO: do some tests if this calculation is correct
export const estimatedConfigSize = (c: KeyboardConfigInternal): number =>
  SIZES.config +
  layersSize(c.layers) +
  mouseConfigSize(c.mouse) +
  ledsConfigSize(c.leds);

// TODO: available flash size
// Estimated size of firmware without config (no layers, no led configs)
const NOCONFIG_FIRMWARE_SIZE = 48 * 1024;
const FLASH_SIZE = 64 * 1024;

export const ESTIMATED_AVAILABLE_SIZE = FLASH_SIZE - NOCONFIG_FIRMWARE_SIZE;

export type HumanSizeInfo = {
  bytes: number;
  divider: number;
  value: number;
  prefix: string;
};

export const humanSize = (bytes: number): HumanSizeInfo => {
  const prefixes = ["", "Ki", "Mi", "Gi", "Ti"];
  let div = 1;
  let i = 0;
  while (i < prefixes.length) {
    if (bytes / div < 1024) break;
    div *= 1024;
    i += 1;
  }
  return {
    bytes,
    divider: div,
    value: bytes / div,
    prefix: prefixes[i],
  };
};
